<template>
  <div class="relative">
    <div class="flex flex-col gap-3" @submit.prevent="handleSubmit">
      <BaseSection
        v-if="isOnSiteInspection || useOrderStore().isSmartHeatingOrder()"
      >
        <BaseToggle
          v-model:is-checked="inspectedBuilding.compatibility.isCompatible"
          label="Gebäude ist nicht SRT-ausstattungsfähig"
          :is-inverted="true"
          :is-disabled="
            useOrderStore().isSmartHeatingOrder() && !isOnSiteInspection
          "
          test-id="toggle_isCompatible"
          :validation="v$.inspectedBuilding.compatibility.isCompatible"
          @is-toggled="onCompatibilityChange($event)"
        />
        <BaseDropdown
          v-if="!isCompatible"
          v-model:selection="inspectedBuilding.compatibility.reason"
          label="Grund"
          dropdown-text="Grund wählen"
          :disabled="
            useOrderStore().isSmartHeatingOrder() && !isOnSiteInspection
          "
          :options-list="compatibilityReasonList"
          :validation="v$.inspectedBuilding.compatibility.reason"
        />
        <BaseTextarea
          v-if="!isCompatible"
          v-model:input-text="inspectedBuilding.compatibility.note"
          label="Notizen"
          :optional="true"
          :is-disabled="
            useOrderStore().isSmartHeatingOrder() && !isOnSiteInspection
          "
          :validation="v$.inspectedBuilding.compatibility.note"
          :max-length="NOTE_MAX_LENGTH"
          test-id="textarea_compatibleNote"
          class="pt-0"
        />
      </BaseSection>
      <AddressOptionList
        v-model:address="building.address"
        v-model:building="building"
        :validation="v$"
      />
      <BaseSection heading="Gebäude Informationen">
        <BaseDropdown
          v-model:selection="building.usageType"
          label="Nutzungsart"
          dropdown-text="Nutzungsart wählen"
          class="md:basis-1/2"
          :options-list="usageTypeList"
          :validation="v$.building.usageType"
          :optional="!isCompatible"
        />
        <div class="md:flex md:gap-4">
          <BaseDropdown
            v-model:selection="building.interiorWallType"
            class="md:basis-1/2"
            label="Innenwände"
            dropdown-text="Innenwände wählen"
            :options-list="interiorWallOptions"
            :validation="v$.building.interiorWallType"
            :optional="!isOnSiteInspection || !isAccessible || !isCompatible"
          />
          <BaseDropdown
            v-model:selection="building.exteriorWallType"
            class="md:basis-1/2"
            label="Außenwände"
            dropdown-text="Außenwände wählen"
            :options-list="exteriorWallOptions"
            :validation="v$.building.exteriorWallType"
            :optional="!isOnSiteInspection || !isCompatible"
          />
        </div>
        <div class="md:flex md:gap-4">
          <BaseDropdown
            v-model:selection="building.floorType"
            class="md:basis-1/2"
            label="Fußboden"
            dropdown-text="Fußboden wählen"
            :options-list="floorOptions"
            :validation="v$.building.floorType"
            :optional="!isOnSiteInspection || !isAccessible || !isCompatible"
          />
          <BaseFormInput
            v-model.number="building.constructionYear"
            label="Baujahr"
            class="md:basis-1/2"
            :validation="v$.building.constructionYear"
            :optional="!isOnSiteInspection || !isCompatible"
            test-id="input_constructionYear"
            type="number"
          />
        </div>
        <BaseSection v-if="!isNewBuilding" heading="Wird vom System ermittelt">
          <div class="md:flex md:gap-4">
            <BaseFormInput
              v-model="buildingMetrics.floorLevels"
              disabled
              class="md:basis-1/3"
              label="Anzahl Stockwerke"
              :optional="false"
            />
            <BaseFormInput
              v-model="buildingMetrics.roomgroups"
              disabled
              class="md:basis-1/3"
              label="Anzahl angemeldeter Mieter"
            />
            <BaseFormInput
              v-model="buildingMetrics.average"
              disabled
              class="md:basis-1/3"
              label="Anzahl Wohnungen pro Geschoß"
            />
          </div>
        </BaseSection>
      </BaseSection>

      <BaseSection
        heading="Fotos (Außenansicht, Gebäude, Treppenhaus, Klingelschild, Fluchtpläne)"
      >
        <ImageCapture
          ref="imageCaptureRef"
          :object-id="building.id"
          :object-type="ImageObjectType.BUILDING"
          :capture-image-information="true"
          :image-keyword-list="buildingImageKeywordList"
        />
      </BaseSection>

      <BaseSection heading="Kontaktperson">
        <div class="md:flex md:gap-4">
          <div class="md:basis-1/2">
            <BaseFormInput
              v-model="building.contactPerson.name"
              label="Name"
              :optional="true"
              test-id="input_contactPersonName"
              :auto-correct="false"
            />
          </div>
          <div class="md:basis-1/2">
            <BaseFormInput
              v-model="building.contactPerson.phoneNumber"
              label="Telefonnummer"
              :optional="true"
              test-id="input_contactPersonPhoneNumber"
            />
          </div>
        </div>
        <BaseTextarea
          v-model:input-text="building.contactPerson.note"
          label="Notizen Kontaktperson"
          :optional="true"
          test-id="textarea_contactPersonNote"
        />
      </BaseSection>
      <BaseSection heading="Weitere Angaben" :required="false">
        <BaseTextarea
          v-model:input-text="building.note"
          label="Notizen"
          :max-length="NOTE_MAX_LENGTH"
          test-id="textarea_note"
        />
        <div class="mt-6">
          <BaseToggle
            v-model:is-checked="building.hasMonumentProtection"
            label="Denkmalschutz"
            test-id="toggle_hasMonumentProtection"
            :required="false"
          />
          <BaseToggle
            v-model:is-checked="building.isAccessible"
            label="Kein Zutritt"
            :is-inverted="true"
            test-id="toggle_isAccessible"
            :required="false"
            @is-toggled="handleIsAccessibleChange($event)"
          />
          <div v-if="!building.isAccessible" class="my-3 gap-3">
            <div class="gap-3 md:flex w-full">
              <BaseDropdown
                :selection="building.reasonNotAccessible"
                label="Grund"
                dropdown-text="Grund wählen"
                :options-list="buildingNotAccessibleReasonsList"
                :validation="v$.building.reasonNotAccessible"
                class="md:basis-4/5"
                @update:selection="handleReasonChange"
              />
              <BaseDateTime
                v-model:date="building.dateNotAccessible"
                :validation="v$.building.dateNotAccessible"
                class="md:basis-1/5"
              />
            </div>
            <BaseRadioGroup
              v-if="building.reasonNotAccessible"
              v-model:selection="building.typeNotAccessible"
              class="flex"
              :options-list="impedimentTypes"
              :validation="v$.building.typeNotAccessible"
              @update:selection="
                building.dateNotAccessible = new Date().toISOString()
              "
            />
          </div>
        </div>
        <ImpedimentHistory :entries="building.impediments ?? []" />
      </BaseSection>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {
  buildingImageKeywordList,
  buildingNotAccessibleReasonsList,
  exteriorWallOptions,
  floorOptions,
  interiorWallOptions,
  compatibilityReasonList,
  usageTypeList,
} from '@/enums/building';
import { Building } from '@/models';
import { NOTE_MAX_LENGTH } from '@/enums/generic';
import { computed, onBeforeMount, Ref, ref } from 'vue';
import { useValidation } from '@/composables/useValidation';
import { useBuildingForm } from '@/composables/useBuildingForm';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import BaseToggle from '@/components/UI/Switch/BaseToggle.vue';
import BaseDateTime from '@/components/UI/Input/BaseDateTime.vue';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { FormAction, useFormStore } from '@/store/form/formStore';
import { useOrderStore } from '@/store/order/orderStore';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import ImageCapture, {
  ImageCaptureRef,
} from '@/components/Forms/DeviceFormComponents/ImageCapture.vue';
import { ImageObjectType } from '@/models/Image';
import AddressOptionList from '@/components/Forms/BuildingFormComponents/AddressOptionList.vue';
import { InspectedBuilding } from '@/models/InspectedBuilding';
import { impedimentTypes } from '@/enums/generic';
import BaseRadioGroup from '@/components/UI/Radio/BaseRadioGroup.vue';
import ImpedimentHistory from '@/components/ImpedimentHistory.vue';

const props = defineProps<{
  node?: Building;
  parentId: string;
  payload?: InspectedBuilding;
}>();
const emit = defineEmits(['update-entity', 'update-entities']);

const imageCaptureRef: Ref<ImageCaptureRef | null> = ref(null);

const {
  building,
  inspectedBuilding,
  initializeBuildingForm,
  handleIsAccessibleChange,
  handleReasonChange,
  handleSubmit,
} = useBuildingForm(props.node, props.parentId, imageCaptureRef, emit);

onBeforeMount(() => {
  initializeBuildingForm();
  handleIsCompatible();
});

const handleIsCompatible = () => {
  if (!props.payload?.compatibility || !inspectedBuilding.value?.compatibility)
    return;
  if (props.payload.compatibility?.isCompatible) {
    inspectedBuilding.value.compatibility.isCompatible = false;
  } else {
    inspectedBuilding.value.compatibility.isCompatible = true;
    onCompatibilityChange(true);
  }
};

const onCompatibilityChange = (isCompatible: boolean) => {
  if (!isCompatible) return;
  if (inspectedBuilding.value.compatibility) {
    inspectedBuilding.value.compatibility.reason = undefined;
    inspectedBuilding.value.compatibility.note = undefined;
  }
};

const isNewBuilding = computed(() => {
  return useFormStore().formAction === FormAction.CREATE;
});

const buildingMetrics = computed(() => {
  return useBuildingStore().getBuildingMetrics();
});

const isOnSiteInspection = computed(() => {
  return useOrderStore().isOnSiteInspection();
});

const isAccessible = computed(() => {
  return building.value.isAccessible;
});

const isCompatible = computed(() => {
  return inspectedBuilding.value?.compatibility?.isCompatible ?? true;
});

const { v$, isFormValid } = useValidation(
  { building, inspectedBuilding },
  { isOnSiteInspection: isOnSiteInspection.value }
);

defineExpose({
  handleSubmit,
  isFormValid,
});
</script>
